<template>
  <v-dialog
    v-model="opened"
    width="500"
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="$emit('update:opened', false)"
  >
    <v-card block flat>
      <v-card-title class="title pb-0">
        <span class="title">Confirm this information</span>
      </v-card-title>
      <v-card-text>
        <v-layout wrap class="text-xs-left" v-if="editableUser">
          <v-flex xs12>
            <v-alert outline :value="true" class="mb-4" type="info">
              {{ $t('questionnaire.profileUpdateMessage') }}
            </v-alert>
          </v-flex>
          <v-flex xs12>
            <div class="text-xs-left">
              {{ $t('profile.primaryContactEmail') }}
            </div>
            <v-text-field
              solo
              :placeholder="$t('profile.email')"
              v-model="editableUser.email"
              class="default-input"
            />
          </v-flex>
          <v-flex v-if="!smsCollectionPopupsDisabled" xs12>
            <div class="text-xs-left">
              {{ $t('profile.cellPhoneNumber') }}
            </div>
            <v-text-field
              solo
              v-model="editableUser.phone_number"
              class="default-input"
              label="(777) 777- 7777"
              mask="phone"
            />
          </v-flex>
          <v-flex xs12>
            <div class="text-xs-left">Current Address</div>
            <address-autocomplete
              id="profile-address"
              :value="editableUser.stringAddress"
              @placechanged="addressChange"
              class-name="default-input"
              placeholder="Start typing"
            />
          </v-flex>

          <v-flex
            xs12
            v-if="
              !isEmpty(editableUser.phone_number) &&
                !smsCollectionPopupsDisabled
            "
          >
            <div class="text-xs-left">
              Don't want to miss any notifications?
            </div>
            <v-checkbox
              hide-details
              solo
              color="red darken-3"
              class="mt-1"
              label="Send you notifications via SMS"
              v-model="editableUser.notify_via_sms"
            >
              <template slot="label">
                <span class="grey--text text--darken-3">
                  Allow us to notify you via SMS
                </span>
              </template>
            </v-checkbox>
          </v-flex>
        </v-layout>
        <v-alert
          class="text-xs-left"
          outline
          :value="true"
          type="info"
          v-if="editableUser.notify_via_sms && !smsCollectionPopupsDisabled"
        >
          By providing this phone number, you agree to receive messages from
          {{ currentFirm.name }}. Message and data rates may apply. Message
          frequency varies.
        </v-alert>
      </v-card-text>
      <v-card-actions class="pb-4 px-4">
        <v-spacer />
        <v-btn
          :block="$vuetify.breakpoint.smAndDown"
          class="primary text-capitalize"
          :loading="savingProfile"
          @click="saveProfile"
        >
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import API from '@/api';
import moment from 'moment';
import AddressAutocomplete from '@/components/AddressAutocomplete';

export default {
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
    currentQuestionnaire: {
      type: Object,
      default: () => {},
    },
    currentCompany: {
      type: Object,
      required: true,
    },
    smsCollectionPopupsDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editableUser: null,
      savingProfile: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
  },
  components: {
    AddressAutocomplete,
  },

  methods: {
    isEmpty,
    moment,
    getStreetAddress(addressObject = {}) {
      const hasStreetInfo = Boolean(
        addressObject && addressObject.street_number && addressObject.route,
      );
      const isValidNorthAmericanAddress = Boolean(
        addressObject &&
          addressObject.locality &&
          addressObject.postal_code &&
          addressObject.administrative_area_level_1 &&
          hasStreetInfo,
      );

      // Don't update with invalid address
      if (!isValidNorthAmericanAddress)
        return {
          street: this.currentCompany.street || '',
          state: this.currentCompany.state || '',
          zip: this.currentCompany.zip || '',
          city: this.currentCompany.city || '',
          country: this.currentCompany.country || '',
        };

      // Update company with valid address only
      return {
        street: `${addressObject.street_number} ${addressObject.route}`,
        state: addressObject.administrative_area_level_1,
        zip: addressObject.postal_code,
        city: addressObject.locality,
        country: addressObject.country,
      };
    },
    addressChange(address) {
      this.editableUser.address = address;
    },
    async saveProfile() {
      this.savingProfile = true;

      try {
        const { street, state, zip, city, country } = this.getStreetAddress(
          this.editableUser.address,
        );

        await API.company.updateCompany({
          id: this.currentCompany.id,
          userId: this.editableUser.id,
          phone_number: this.editableUser.phone_number,
          email: this.editableUser.email,
          street: street,
          state: state,
          zip: zip,
          city: city,
          country: country,
        });

        const userPrefersSms = Boolean(
          this.editableUser.notify_via_sms && this.editableUser.phone_number,
        );
        const newPreferredContactMethod = userPrefersSms ? 'sms' : 'email';

        await API.user.updateUser({
          id: this.currentUser.id,
          preferred_contact_method: newPreferredContactMethod,
        });

        await API.questionnaire.markProfileUpdated({
          companyId: this.currentQuestionnaire.company_id,
          id: this.currentQuestionnaire.id,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Profile saved!',
        });
        this.$emit('update:opened', false);
      } catch (error) {
        console.error('Error saving client information');
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Error saving client information',
        });
      } finally {
        this.savingProfile = false;
      }
    },
  },
  created() {
    if (this.currentCompany) {
      let stringAddress = '';
      if (this.currentCompany.street) {
        stringAddress += this.currentCompany.street;
      }

      if (this.currentCompany.city) {
        stringAddress += stringAddress
          ? `, ${this.currentCompany.city}`
          : this.currentCompany.city;
      }

      if (this.currentCompany.state) {
        stringAddress += stringAddress
          ? `, ${this.currentCompany.state}`
          : this.currentCompany.state;
      }

      if (this.currentCompany.zip) {
        stringAddress += stringAddress
          ? `, ${this.currentCompany.zip}`
          : this.currentCompany.zip;
      }

      if (this.preview) {
        this.editableUser = {
          id: null,
          phone_number: null,
          email: null,
          address: null,
          stringAddress,
          notify_via_sms: false,
        };
      } else {
        if (this.currentUser.is_advisor) {
          this.editableUser = {
            id: this.currentCompany.user.id,
            phone_number: this.currentCompany.user.phone_number,
            email: this.currentCompany.user.email,
            address: null,
            stringAddress,
            notify_via_sms: false,
          };
        } else {
          this.editableUser = {
            id: this.currentUser.id,
            phone_number: this.currentUser.phone_number,
            email: this.currentUser.email,
            address: null,
            stringAddress,
            notify_via_sms: false,
          };
        }
      }
    }
  },
};
</script>

<style lang="postcss" scoped>
.outlined-red {
  border: 1px solid #cb4039;
}
</style>
